import {Show, Text, VStack} from 'platform/foundation';

import {ForwardedRef, forwardRef, useMemo} from 'react';

import {suffixTestId} from 'shared/web';

import {Choice} from '../Choice/Choice';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {EditorContainer, PlaceholderText, StyledEditor} from './components/EditorArea';
import {
  EmojiButton,
  EmojiCategoryButton,
  EmojiCategoryTabs,
  EmojiGrid,
  EmojiPanel,
  EmojiPickerWrapper,
} from './components/EmojiPicker';
import {FieldWrapper} from './components/FieldWrapper';
import {FooterBar, FooterCounter} from './components/FooterBar';
import {
  Toolbar,
  ToolbarButton,
  ToolbarChoiceWrapper,
  ToolbarGroup,
  ToolbarIcon,
} from './components/Toolbar';
import {getEmojiCategories} from './constants/emojiCategories';
import {getEmojiTriggerDefinition} from './constants/emojiTriggerDefinition';
import {getFormatOptions} from './constants/formatOptions';
import {getToolbarButtonRegistry} from './constants/toolbarButtonRegistry';
import {useRichTextEditor} from './hooks/useRichTextEditor';
import {RichTextProps} from './types';
import {buildCounterLabel} from './utils/buildCounterLabel';
import {formatFormatOptionLabel} from './utils/formatFormatOptionLabel';

export const RichText = forwardRef(
  (props: RichTextProps, userRef: ForwardedRef<HTMLDivElement>) => {
    const t = useTranslationContext();

    const formatOptions = useMemo(() => getFormatOptions(t), [t]);
    const emojiCategories = useMemo(() => getEmojiCategories(t), [t]);
    const toolbarButtonRegistry = useMemo(() => getToolbarButtonRegistry(t), [t]);
    const emojiTriggerDefinition = useMemo(() => getEmojiTriggerDefinition(t), [t]);

    const {
      id,
      helperId,
      ref,
      toolbarRef,
      pickerRef,
      selectionPath,
      activeCommands,
      selectedFormat,
      isFocused,
      isEmojiPickerOpen,
      activeEmojiCategory,
      setActiveEmojiCategory,
      setIsEmojiPickerOpen,
      toolbarGroups,
      currentLength,
      isOverLimit,
      isNearLimit,
      isEmpty,
      handleInput,
      handleFocus,
      handleBlur,
      handleKeyDown,
      handlePaste,
      handleToolbarKeyDown,
      handleCommandClick,
      handleFormatChange,
      handleEmojiInsert,
      preventToolbarBlur,
      captureSelectionBeforeBlur,
    } = useRichTextEditor(props, userRef);

    return (
      <VStack>
        <Label
          id={id}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('label', props)}
          actions={props.labelActions}
          maxLength={props.maxLength}
          isCounterVisible={props.isCounterVisible}
          currentLength={currentLength}
        >
          {props.label}
        </Label>
        <FieldWrapper
          $isDisabled={props.isDisabled}
          $isInvalid={props.isInvalid || !!props.errorMessage}
        >
          <Toolbar
            ref={toolbarRef}
            role="toolbar"
            aria-label={t('richText.toolbar.formattingToolbar')}
            onKeyDown={handleToolbarKeyDown}
          >
            {toolbarGroups.map((group, groupIndex) => (
              <ToolbarGroup key={`toolbar-group-${groupIndex}`}>
                {group.map((item) => {
                  if ('formatselect' === item) {
                    return (
                      <ToolbarChoiceWrapper key={item} onMouseDown={captureSelectionBeforeBlur}>
                        <Choice
                          value={selectedFormat}
                          onChange={handleFormatChange}
                          options={formatOptions}
                          formatOptionLabel={formatFormatOptionLabel}
                          isSearchable={false}
                          isNotClearable
                          isDisabled={props.isDisabled}
                          menuInPortal
                          data-testid={suffixTestId('formatSelect', props)}
                        />
                      </ToolbarChoiceWrapper>
                    );
                  }

                  if ('emoji' === item) {
                    return (
                      <EmojiPickerWrapper key={item} ref={pickerRef}>
                        <ToolbarButton
                          type="button"
                          data-rich-text-toolbar-item="true"
                          aria-label={emojiTriggerDefinition.ariaLabel}
                          onMouseDown={preventToolbarBlur}
                          onClick={() => setIsEmojiPickerOpen((value) => !value)}
                          disabled={props.isDisabled}
                          data-testid={suffixTestId('emojiButton', props)}
                        >
                          <ToolbarIcon>{emojiTriggerDefinition.icon}</ToolbarIcon>
                        </ToolbarButton>
                        <Show when={isEmojiPickerOpen && !props.isDisabled}>
                          <EmojiPanel data-testid={suffixTestId('emojiPicker', props)}>
                            <EmojiCategoryTabs>
                              {emojiCategories.map((category, categoryIndex) => (
                                <EmojiCategoryButton
                                  key={category.label}
                                  type="button"
                                  $isActive={categoryIndex === activeEmojiCategory}
                                  onMouseDown={preventToolbarBlur}
                                  onClick={() => setActiveEmojiCategory(categoryIndex)}
                                >
                                  {category.label}
                                </EmojiCategoryButton>
                              ))}
                            </EmojiCategoryTabs>
                            <EmojiGrid>
                              {emojiCategories[activeEmojiCategory].emojis.map((emoji) => (
                                <EmojiButton
                                  key={emoji}
                                  type="button"
                                  onMouseDown={preventToolbarBlur}
                                  onClick={() => handleEmojiInsert(emoji)}
                                  aria-label={t('richText.toolbar.insertEmojiChar', emoji)}
                                >
                                  {emoji}
                                </EmojiButton>
                              ))}
                            </EmojiGrid>
                          </EmojiPanel>
                        </Show>
                      </EmojiPickerWrapper>
                    );
                  }

                  const button = toolbarButtonRegistry[item];

                  return (
                    <ToolbarButton
                      key={item}
                      type="button"
                      data-rich-text-toolbar-item="true"
                      aria-label={button.ariaLabel}
                      aria-pressed={activeCommands[button.command] ?? false}
                      $isActive={activeCommands[button.command] ?? false}
                      onMouseDown={preventToolbarBlur}
                      onClick={() => handleCommandClick(button.command)}
                      disabled={props.isDisabled}
                    >
                      <ToolbarIcon>{button.icon}</ToolbarIcon>
                    </ToolbarButton>
                  );
                })}
              </ToolbarGroup>
            ))}
          </Toolbar>
          <EditorContainer>
            <Show when={isEmpty && !isFocused && !!props.placeholder}>
              <PlaceholderText>{props.placeholder}</PlaceholderText>
            </Show>
            <StyledEditor
              id={id}
              ref={ref}
              role="textbox"
              aria-label={props.label ?? undefined}
              aria-multiline="true"
              aria-describedby={props.errorMessage || props.helperText ? helperId : undefined}
              aria-invalid={props.isInvalid || !!props.errorMessage}
              aria-disabled={props.isDisabled}
              contentEditable={!props.isDisabled}
              suppressContentEditableWarning
              spellCheck
              tabIndex={props.isDisabled ? -1 : 0}
              $isDisabled={props.isDisabled}
              $isResizable={props.isResizable}
              $isScrollable={props.isScrollable}
              $rows={props.rows}
              $minRows={props.minRows}
              $maxRows={props.maxRows}
              onInput={handleInput}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              data-testid={suffixTestId('richTextInput', props)}
            />
          </EditorContainer>
          <FooterBar>
            <Text size="xSmall" color="secondary">
              {selectionPath || 'p'}
            </Text>
            <Show when={props.isCounterVisible !== false}>
              <FooterCounter $isOverLimit={isOverLimit} $isNearLimit={isNearLimit}>
                {buildCounterLabel(currentLength, props.maxLength)}
              </FooterCounter>
            </Show>
          </FooterBar>
        </FieldWrapper>
        <Show when={props.errorMessage || props.helperText}>
          <div id={helperId}>
            <HelperText
              errorMessage={props.errorMessage}
              helperText={props.helperText}
              data-testid={suffixTestId('helper', props)}
            />
          </div>
        </Show>
      </VStack>
    );
  }
);
